import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';
import service from 'utils/service';

// assets
import { IconBell } from '@tabler/icons-react';

// notification status options
const status = [
  { value: 'all', label: 'All Notification' },
  { value: 'unread', label: 'Unread' }
];

// ==============================|| NOTIFICATION TYPES ||============================== //

export interface NotificationItem {
  ID: number;
  RECIPIENT_ID: number;
  RECIPIENT_TYPE: string;
  NOTIFICATION_TOPIC: string;
  TITLE: string;
  MESSAGE: string;
  IS_READ: number;
  IS_SHOWN: number;
  IS_ARCHIVED: number;
  CREATED_AT: string;
  UPDATED_AT: string;
}

// ==============================|| NOTIFICATION ||============================== //

export default function NotificationSection() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);




  const [loading, setLoading] = useState(false);
  // Badge count — directly from getUnopenedCount API
  const [unopenedCount, setUnopenedCount] = useState(0);


  const anchorRef = useRef<any>(null);

  // ── Fetch unread count from API ───────────────────────────────────────────
  // Called on login + every 10 min. API itself returns the latest unread count.
  // We simply replace the badge with whatever the API returns — no addition.
  const fetchUnopenedCount = useCallback(async () => {
    try {
      const response: any = await service.makeAPICall({
        methodName: service.Methods.POST,
        apiUrl: service.API_URL.notifications.getUnopenedCount
      });
      if (response?.code === 200) {
        setUnopenedCount(response.count ?? 0);
      }
    } catch {
      // silently ignore
    }
  }, []);

  // ── Load notification list (for panel) ───────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await service.makeAPICall({
        methodName: service.Methods.POST,
        apiUrl: service.API_URL.notifications.getNotifications
      });
      if (response?.code === 200) {
        setNotifications(Array.isArray(response.data) ? response.data : []);
      }
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Mark single notification as read ─────────────────────────────────────
  const handleMarkAsRead = useCallback(async (id: number) => {
    // Optimistically update UI
    setNotifications((prev) =>
      prev.map((n) => (n.ID === id ? { ...n, IS_READ: 1 } : n))
    );
    try {
      await service.makeAPICall({
        methodName: service.Methods.POST,
        apiUrl: service.API_URL.notifications.markAsRead,
        body: { ID: id }
      });
    } catch {
      // Revert on failure
      setNotifications((prev) =>
        prev.map((n) => (n.ID === id ? { ...n, IS_READ: 0 } : n))
      );
    }
  }, []);

  // ── Mark all notifications as read ────────────────────────────────────────
  const handleMarkAllRead = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    setNotifications((prev) => prev.map((n) => ({ ...n, IS_READ: 1 })));
    setUnopenedCount(0);
    try {
      await service.makeAPICall({
        methodName: service.Methods.POST,
        apiUrl: service.API_URL.notifications.markAllRead
      });
    } catch {
      // Silently ignore; a refresh will correct state
    }
  }, []);

  // On login + every 10 min: call getUnopenedCount API → replace badge with API value
  useEffect(() => {
    fetchUnopenedCount();
    const interval = setInterval(fetchUnopenedCount, 600_000);
    return () => clearInterval(interval);
  }, [fetchUnopenedCount]);

  // When panel opens: reset badge to 0 + fetch notification list
  useEffect(() => {
    if (open) {
      setUnopenedCount(0);
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
    event?.target.value && setValue(event?.target.value);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  // ── Filter notifications based on status dropdown ─────────────────────────
  const filteredNotifications = notifications.filter((n) => {
    if (value === 'unread') return n.IS_READ === 0;
    if (value === 'new') return n.IS_READ === 0;
    return true; // 'all' or default
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Fragment>
      {/* Bell icon with unread badge */}
      <Box sx={{ ml: 2 }}>
        <Badge
          badgeContent={unopenedCount > 0 ? unopenedCount : undefined}
          color="error"
          max={99}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 18, minWidth: 18 } }}
        >
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              transition: 'all .2s ease-in-out',
              color: theme.vars.palette.warning.dark,
              background: theme.vars.palette.warning.light,
              '&:hover, &[aria-controls="menu-list-grow"]': {
                color: theme.vars.palette.warning.light,
                background: theme.vars.palette.warning.dark
              },
              ...theme.applyStyles('dark', {
                color: theme.vars.palette.warning.dark,
                background: theme.vars.palette.dark.main,
                '&:hover, &[aria-controls="menu-list-grow"]': {
                  color: theme.vars.palette.grey[800],
                  background: theme.vars.palette.warning.dark
                }
              })
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
          >
            <IconBell stroke={1.5} size="20px" />
          </Avatar>
        </Badge>
      </Box>

      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [downMD ? 5 : 0, 20] } }]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
              <Paper>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]} sx={{ maxWidth: 330 }}>
                  <Stack sx={{ gap: 2 }}>
                    {/* Header */}
                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', pt: 2, px: 2 }}>
                      <Stack direction="row" sx={{ gap: 2 }}>
                        <Typography variant="subtitle1">All Notification</Typography>
                        <Chip
                          size="small"
                          label={String(notifications.filter((n) => n.IS_READ === 0).length).padStart(2, '0')}
                          variant="filled"
                          sx={{ color: 'background.default', bgcolor: 'warning.dark' }}
                        />
                      </Stack>
                      <Typography
                        component="span"
                        variant="subtitle2"
                        sx={{ color: 'primary.main', cursor: 'pointer' }}
                        onClick={handleMarkAllRead}
                      >
                        Mark as all read
                      </Typography>
                    </Stack>

                    <Box sx={{ height: 1, maxHeight: 'calc(100vh - 205px)', overflowX: 'hidden', '&::-webkit-scrollbar': { width: 5 } }}>
                      {/* Status filter dropdown */}
                      <Box sx={{ px: 2, pt: 0.25 }}>
                        <TextField
                          id="outlined-select-currency-native"
                          select
                          fullWidth
                          value={value}
                          onChange={handleChange}
                          slotProps={{ select: { native: true } }}
                        >
                          {status.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </TextField>
                      </Box>
                      <Divider sx={{ mt: 2 }} />

                      {loading ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                          <CircularProgress size={28} />
                        </Stack>
                      ) : (
                        <NotificationList notifications={filteredNotifications} onMarkAsRead={handleMarkAsRead} onClose={() => setOpen(false)} />
                      )}
                    </Box>
                  </Stack>

                  {/* <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                    <Button size="small" disableElevation>
                      View All
                    </Button>
                  </CardActions> */}
                </MainCard>
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </Fragment>
  );
}
